import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { userStatusService } from '../services/userStatusService';
import { useAuthContext } from './AuthContext';
import { UserStatusApiResponse } from '../types';

// Poll every 30 seconds while the professional is not yet active
const POLL_INTERVAL_MS = 30_000;

interface UserStatusContextType {
  userStatusData: UserStatusApiResponse | null;
  loading: boolean;
  error: string | null;
  fetchUserStatus: (isBackground?: boolean) => Promise<void>;
}

const UserStatusContext = createContext<UserStatusContextType | undefined>(undefined);
export const UserStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthContext();
  const [userStatusData, setUserStatusData] = useState<UserStatusApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchUserStatus = useCallback(async (isBackground = false) => {
    if (user?.type_account !== 'professional') {
      if (!isBackground) setLoading(false);
      setUserStatusData(null); 
      return;
    }

    try {
      if (!isBackground) setLoading(true);
      if (!isBackground) setError(null);

      const response = await userStatusService.getStatus();

      if (response.success && response.data) {
        setUserStatusData(response.data);
      } else {
        if (!isBackground) setError(response.message || 'Error al cargar el estado del usuario.');
      }
    } catch (err: any) {
      console.error('Error en UserStatusContext:', err);
      if (!isBackground) setError(err.message || 'No se pudo conectar con el servidor.');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchUserStatus();
  }, [fetchUserStatus]);

  // Poll while the professional status is not yet 'active'
  useEffect(() => {
    const shouldPoll =
      user?.type_account === 'professional' &&
      userStatusData !== null &&
      userStatusData.status !== 'active';

    if (shouldPoll) {
      pollTimerRef.current = setInterval(() => {
        // Only poll when the tab is visible to avoid unnecessary requests
        if (document.visibilityState === 'visible') {
          fetchUserStatus(true);
        }
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [user, userStatusData, fetchUserStatus]);

  const value = {
    userStatusData,
    loading,
    error,
    fetchUserStatus,
  };

  return (
    <UserStatusContext.Provider value={value}>
      {children}
    </UserStatusContext.Provider>
  );
};

export const useUserStatus = () => {
  const context = useContext(UserStatusContext);
  if (context === undefined) {
    throw new Error('useUserStatus debe ser usado dentro de un UserStatusProvider');
  }
  return context;
};